import { useMsal } from "@azure/msal-react";
import { FunctionComponent, useEffect, useState } from "react";
import { Button, ListGroup } from "react-bootstrap";
import { FormattedMessage, useIntl } from 'react-intl';
import { getUser, saveUser } from "../api/User";
import { ICompany, IUserCompanies, IUserProfile } from "../util/DataModelInterface";
import { getCompany } from '../api/Company';
import { Link } from "react-router-dom";
import { ToastWrap } from '../components/ToastWrap';
import { Spinner } from "../components/Spinner";
import useToastHandler from "../hooks/useToastHandler";

export const Profile: FunctionComponent = () => {
  const { instance } = useMsal();
  const intl = useIntl();
  const { addToast } = useToastHandler();

  const [user, setUser] = useState({} as IUserProfile);
  const [language, setLanguage] = useState(user.language);
  const [phone, setPhone] = useState(user.phone);
  const [companies, setCompanies] = useState([] as ICompany[]);
  const [changed, setChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandDetails, setExpandDetails] = useState(false);
  const [expandCompanies, setExpandCompanies] = useState(false);
  // const [expandHistory, setExpandHistory] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastHeader, setToastHeader] = useState("");
  const [toastText, setToastText] = useState("");
  const [toastType, setToastType] = useState("");


  useEffect(() => {
    const onLoad = async () => {
      let activeAccount = instance.getActiveAccount();
      if(activeAccount) {
          const userData = await getUser();
          if (userData?.error) {
            addToast(userData.error, 'users', 'fetch');
          } else {
            // console.log("Profile - user: ", userData);
            setUser(userData ? userData : {} as IUserProfile);
            setLanguage(userData ? userData.language : 'FI');
            setPhone(userData ? userData.phone : '');
          }
      }
    }
    onLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if(user.phone === phone && user.language === language)
      setChanged(false);
    else setChanged(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, language]);

  useEffect(() => {
    if (user?.companies?.length) {
      let userCompanies = [] as ICompany[];
      const onLoad = async () => {
        await Promise.all(user.companies.map(async (c: IUserCompanies) => {
            let result = await getCompany(c.id);
            if (result?.error) {
              addToast(result.error, 'users', 'fetch');
            } else {
              userCompanies.push(result);
            }
        }));
        setCompanies(userCompanies);
      };
      onLoad();

    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveChanges = async () => {

    setSaving(true);
    let result = await saveUser({...user, language: language, phone: phone});
    if (result?.error) {
      addToast(result.error, 'users', 'modify');
    } else {
      setChanged(false);
      setToastHeader(intl.formatMessage({id: 'users.saved'}));
      setToastText("");
      setToastType('success');
      setToastActive(true);
    }
    setSaving(false);
  }

  const getCompanyItem = (company: ICompany, ind: number) => {
    return (
        <ListGroup.Item key={ind}>
          <div className="company">
            <span className="title">
              <Link to='/profile/companyinfo' state={{ company }} replace={false}>
                { company?.name }
              </Link>
            </span>
          </div>
        </ListGroup.Item>
    );
  }

  return (
    <>
    <Spinner active={saving} />
    <ToastWrap active={toastActive} heading={toastHeader} text={toastText} type={toastType} setActive={(active: boolean) => setToastActive(active)} />
    { !user.role &&

    <div className="alert alert-warning w-100">
      <div className="d-flex flex-row">
          <i className="fa fa-warning me-2"></i>
          <div>
          <p>User role missing</p>
          </div>
      </div>
    </div>
  
    }
    { user.role &&
      <div>
        <h1>{user.name}</h1>
        {/* TODO: < p className="info"><FormattedMessage id="profile.lastvisited" /><span className="timestamp">12.2.2023 klo 12:25</span></p> */ }

        <div className="controls d-flex justify-content-end my-3">
          <button className="btn btn-link p-0" style={ !(expandDetails&&expandCompanies/*&&expandHistory*/) ? {} : {display: 'none'}}
            onClick={ () => { setExpandCompanies(true); setExpandDetails(true); }}>
            <FormattedMessage id="button.expandall" /><i aria-hidden="true" className="fa fa-chevron-down ms-2"></i>
          </button>
          <button className="btn btn-link p-0" style={ expandDetails&&expandCompanies/*&&expandHistory*/ ? {} : {display: 'none'}}
            onClick={ () => { setExpandCompanies(false); setExpandDetails(false); }}>
            <FormattedMessage id="button.collapseall" /><i aria-hidden="true" className="fa fa-chevron-up ms-2"></i></button>
        </div>
        <details id="details" open={expandDetails}>
          <summary onClick={ (e) => { setExpandDetails(expandDetails => !expandDetails); e.preventDefault(); }}>
            <span><FormattedMessage id="profile.userinfo" /></span>
          </summary>
          <div>
            <p className="details-info">
              <span className="key"><FormattedMessage id="profile.name" /></span>
              <span className="value">{user.name}</span>
            </p>
            <p className="details-info">
              <span className="key"><FormattedMessage id="profile.email" /></span>
              <span className="value">{user.user_name}</span>
            </p>
            <p className="details-info">
              <span className="key"><FormattedMessage id="profile.role" /></span>
              <span className="value"><FormattedMessage id={'roles.' + user.role}/></span>
            </p>
            <div className="form-group">
              <label htmlFor="phone" className="form-label"><FormattedMessage id="profile.phone" /></label>
              <input id="phone" className="form-control" type="text" value={phone} onChange={(e) => {setPhone(e.target.value)}}/>
            </div>
            <div className="form-group" role="group" aria-labelledby="languagesetlabel">
              <span id="languagesetlabel" className="form-label"><FormattedMessage id="profile.defaultlanguage" /></span>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="languageset" id="languageset_0" value="fi" checked={language==='FI'} 
                       onChange={(e) => {setLanguage('FI')}}/>
                <label className="form-check-label" htmlFor="languageset_0" lang="fi">
                  <FormattedMessage id="language.finnish" />
                </label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="languageset" id="languageset_1" value="sv" checked={language==='SE'}
                       onChange={(e) => {setLanguage('SE')}}/>
                <label className="form-check-label" htmlFor="languageset_1" lang="sv">
                  <FormattedMessage id="language.swedish" />
                </label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="languageset" id="languageset_2" value="en" checked={language==='EN'}
                       onChange={(e) => {setLanguage('EN')}}/>
                <label className="form-check-label" htmlFor="languageset_2" lang="en">
                  <FormattedMessage id="language.english" />
                </label>
              </div>
            </div>
            <div className="form-buttons">
              <Button className="small-cancel-button btn btn-secondary ml-auto"
                      onClick={ async () => { 
                        setExpandDetails(false);
                        setLanguage(user.language);
                        setPhone(user.phone);
                        setChanged(false);
                      }}>
                <FormattedMessage id="button.close" />
              </Button>
              <Button className="small-save-button btn btn-primary" disabled={!changed} 
                      onClick={ async () => { saveChanges() }}>
                        <FormattedMessage id={saving ? "button.saving" : "button.save"} />
              </Button>
            </div>
          </div>
        </details>

        <details id="companies" open={expandCompanies}>
          <summary onClick={ (e) => { setExpandCompanies(expandCompanies => !expandCompanies); e.preventDefault(); }}>
            <span><FormattedMessage id="profile.usercompanies" /></span>
          </summary>
          <div>
            <ListGroup variant="flush">
              {companies?.length && companies.map((c: ICompany, ind: number) => {
                  return getCompanyItem(c, ind)
              }
              )}
            </ListGroup>
          </div>
        </details>

      {/* TODO: later
        <details id="history" open={expandHistory}>
          <summary onClick={ () => {setExpandHistory(expandHistory => !expandHistory)}}>
            <FormattedMessage id="profile.userhistory" />
          </summary>
          <div>
            <p>TODO: esimerkki</p>
            <ul className="list-group list-group-flush">
              <li className="list-group-item">
                <span className="timestamp">12.2.2023 klo 12:25</span>
                <span className="action-type">Kirjautuminen</span>
                <span className="action-content">Käyttäjä kirjautui järjestelmään</span>
              </li>
              <li className="list-group-item">
                <span className="timestamp">12.1.2023 klo 12:25</span>
                <span className="action-type">Raportointi</span>
                <span className="action-content">Käyttäjä lähetti raportin: [raportin nimi]</span>
              </li>
              <li className="list-group-item">
                <span className="timestamp">12.1.2023 klo 12:25</span>
                <span className="action-type">Kirjautuminen</span>
                <span className="action-content">Käyttäjä kirjautui järjestelmään</span>
              </li>
            </ul>
          </div>
        </details> */}

    </div>
    }
    </>
    );
};